// ============================================================================
// RemunerationTab — onglet REMUNERATION de l'ecran Admin (AD-D)
// ============================================================================
// ~70% CLIENT : AUCUNE route serveur nouvelle. Selection d'une cible via la
// liste AD-C (useAdminMembers / listEmployeeProfilesAdmin), puis vue +
// ecritures via /api/management/:email/* (deja gardees ; admin OK via
// canManageEmployee). JAMAIS un 2e chemin sous /api/admin (FF-PII-05).
//
// Distinction AD-D vs AD-C (a ne PAS re-litiger) :
//   AD-C = portes ADMIN-WIDE (create fiche, is_employed, inactivations…)
//   AD-D = par-cible : reutilise le grant CompensationManageGrant existant.
//
// [!] P-ADM-16 : ClassAssignForm hydrate off_grid_reason_* / role_fit_note
// depuis getManagementCompensationView avant tout PUT (UPDATE plein).
// [!] P-ADM-9 : bonus target/actual = fractions 0..1 en lecture, points 0..100
// a l'UI (BonusCard) — convention exacte du hub gestion, pas de *100 fantome.
//
// Formulaires : reutilisent ClassGrid / RealRateCard / remuneration-cards
// (set-compensation, rate, salary-history, bonus, benefits, work/hours/premiums)
// deja cables sur useManagement. Selection = GsSelect (AD-0).

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { DollarSign, Eye, UserRound } from 'lucide-react';
import {
  EmptyState,
  GsSelect,
  QueryError,
  SkeletonCard,
  GS_COMPONENTS,
} from '@gs/ui';
import { ApiForbiddenError, ApiServerError } from '@gs/api-client';
import { useTranslation } from '../../i18n';
import type { AdminMember, AdminMembersResponse } from '../../hooks/useAdminMembers';
import { useAdminRemunerationConfig } from '../../hooks/useAdminRemunerationConfig';
import { useManagement } from '../../hooks/useManagement';
import { ClassGrid } from '../employee/ClassGrid';
import { RealRateCard } from '../employee/RealRateCard';
import {
  RatePremiumCard,
  SalaryHistoryDialog,
  HoursCard,
  BenefitsCard,
  BonusCard,
} from '../employee/remuneration-cards';
import { annualRealPayroll } from '../../lib/salary/payroll';
import { buildHubTimeline, toRealRates } from '../employee/compute';
import { SectionHeader } from './SectionHeader';
// AD-D2 — volet CONFIGURATION (sous-onglet D1). Le volet PAR EMPLOYE reste
// le defaut. Cartes employee/* reutilisees telles quelles (fiche profil).
import { RemunerationConfigPanel } from './RemunerationConfigPanel';

const CAD0 = new Intl.NumberFormat('fr-CA', {
  style: 'currency',
  currency: 'CAD',
  maximumFractionDigits: 0,
});
const money = (n: number) => CAD0.format(n);

function memberLabel(m: AdminMember): string {
  return m.fullName?.trim() ? `${m.fullName} (${m.email})` : m.email;
}

export function RemunerationTab({
  isLoading,
  isError,
  data,
  onRetry,
}: {
  isLoading: boolean;
  isError: boolean;
  data: AdminMembersResponse | undefined;
  onRetry: () => void;
}) {
  const { t } = useTranslation();
  const [subTab, setSubTab] = useState<'employee' | 'config'>('employee');
  const [selectedEmail, setSelectedEmail] = useState('');
  const [banner, setBanner] = useState<string | null>(null);
  const onError = (code: string) => setBanner(code);
  const remuConfig = useAdminRemunerationConfig(true);
  const confirmableCount = (remuConfig.data?.pending ?? []).filter((p) => !p.offGrid).length;

  const options = useMemo(() => {
    const members = data?.members ?? [];
    return [...members]
      .sort((a, b) => memberLabel(a).localeCompare(memberLabel(b), 'fr'))
      .map((m) => ({ value: m.email, label: memberLabel(m) }));
  }, [data?.members]);

  const selected = useMemo(
    () => (data?.members ?? []).find((m) => m.email === selectedEmail) ?? null,
    [data?.members, selectedEmail],
  );

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4">
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }
  if (isError || !data) {
    return <QueryError message={t('gest:admin.remuneration.loadError')} onRetry={onRetry} />;
  }

  const tabCls = (active: boolean) =>
    `touch-manipulation rounded-lg px-3 py-2 text-field transition-colors ${
      active
        ? 'bg-brand-50 font-medium text-brand-600 dark:bg-brand-950 dark:text-brand-400'
        : 'text-slate-500 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-800'
    }`;

  return (
    <div className="flex flex-col gap-5">
      <div
        className="flex flex-wrap gap-1 rounded-xl border border-slate-200 p-1 dark:border-slate-700"
        role="tablist"
        aria-label={t('gest:admin.remuneration.subTabs.label')}
      >
        <button
          type="button"
          role="tab"
          aria-selected={subTab === 'employee'}
          className={tabCls(subTab === 'employee')}
          onClick={() => setSubTab('employee')}
        >
          {t('gest:admin.remuneration.subTabs.employee')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={subTab === 'config'}
          className={`${tabCls(subTab === 'config')} inline-flex items-center`}
          onClick={() => setSubTab('config')}
        >
          {t('gest:admin.remuneration.subTabs.config')}
          {confirmableCount > 0 ? (
            <span className="ml-1.5 inline-flex min-w-5 items-center justify-center rounded-full bg-brand-100 px-1.5 text-chip font-medium text-brand-700 dark:bg-brand-900 dark:text-brand-300">
              {confirmableCount}
            </span>
          ) : null}
        </button>
      </div>

      {subTab === 'config' ? (
        <RemunerationConfigPanel members={data.members} teams={data.teams} />
      ) : (
      <section className={`${GS_COMPONENTS.card} flex flex-col gap-5 p-5`}>
      <SectionHeader
        title={t('gest:admin.remuneration.title')}
        description={t('gest:admin.remuneration.description')}
      />

      <div className="flex flex-col gap-1.5 sm:max-w-lg">
        <span className="text-meta font-medium text-slate-600 dark:text-slate-300">
          {t('gest:admin.remuneration.selectLabel')}
        </span>
        <GsSelect
          value={selectedEmail}
          onChange={(email) => {
            setSelectedEmail(email);
            setBanner(null);
          }}
          options={options}
          placeholder={t('gest:admin.remuneration.selectPlaceholder')}
          ariaLabel={t('gest:admin.remuneration.selectLabel')}
        />
        <p className="text-chip text-slate-500 dark:text-slate-400">
          {t('gest:admin.remuneration.selectHint')}
        </p>
      </div>

      {!selectedEmail ? (
        <EmptyState
          icon={<UserRound size={48} aria-hidden="true" />}
          title={t('gest:admin.remuneration.emptyTitle')}
          description={t('gest:admin.remuneration.emptyDescription')}
        />
      ) : (
        <TargetRemuneration
          key={selectedEmail}
          email={selectedEmail}
          member={selected}
          banner={banner}
          onDismissBanner={() => setBanner(null)}
          onError={onError}
        />
      )}
      </section>
      )}
    </div>
  );
}

function TargetRemuneration({
  email,
  member,
  banner,
  onDismissBanner,
  onError,
}: {
  email: string;
  member: AdminMember | null;
  banner: string | null;
  onDismissBanner: () => void;
  onError: (code: string) => void;
}) {
  const { t } = useTranslation();
  // Hub gestion existant — meme DTO que /profile?tab=gestion (FF-PII-05 chemin unique).
  const q = useManagement(email, true);

  if (q.isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4">
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }
  if (q.isError && q.error instanceof ApiForbiddenError) {
    return (
      <p className="text-field text-slate-500 dark:text-slate-400">
        {t('gest:admin.remuneration.forbidden')}
      </p>
    );
  }
  if (q.isError && q.error instanceof ApiServerError && q.error.status === 404) {
    return (
      <p className="text-field text-slate-500 dark:text-slate-400">
        {t('gest:admin.remuneration.notFound')}
      </p>
    );
  }
  if (q.isError || !q.data) {
    return (
      <QueryError
        message={t('gest:admin.remuneration.targetLoadError')}
        onRetry={() => void q.refetch()}
      />
    );
  }

  const d = q.data;
  const comp = d.compensation;
  const record = comp.record;
  const year = d.year;
  const hub = buildHubTimeline(comp, year);
  const cls = hub?.cls ?? null;
  const specialRate = hub?.timeline?.currentHourly ?? record?.currentHourlyRate ?? null;
  const baseWeekly = comp.work?.weeklyHours ?? cls?.baseHoursPerWeek ?? 40;
  const overrides = [...comp.overrides];
  const premiums = [...comp.premiums];

  const realPayrollNow =
    record && cls
      ? annualRealPayroll({
          year,
          rates: toRealRates(comp.rateHistory),
          weeklyHours: comp.work?.weeklyHours ?? cls.baseHoursPerWeek ?? 40,
          weeksPerYear: cls.weeksPerYear ?? 52,
          hireDate: record.hireDate,
          departureDate: record.departureDate,
        })
      : null;
  const bonusBase =
    realPayrollNow && !realPayrollNow.unpriced
      ? realPayrollNow.total
      : (hub?.timeline?.currentAnnual ?? 0);

  const displayName = member?.fullName?.trim() || d.target.fullName?.trim() || email;
  // PDR-EQU-04 : retour profil -> /admin?tab=remuneration (back-context).
  const profileHref = `/profile/${encodeURIComponent(email)}?tab=gestion&from=admin-remuneration`;

  return (
    <div className="flex flex-col gap-5">
      {banner ? (
        <div
          role="alert"
          className="flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-field text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
        >
          <span>
            {t(`gest:management.errors.${banner}`, {
              defaultValue: t('gest:management.errors.server'),
            })}
          </span>
          <button type="button" onClick={onDismissBanner} className="text-chip font-medium underline">
            {t('gest:management.common.dismiss')}
          </button>
        </div>
      ) : null}

      <div className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900/40">
        <div className="min-w-0">
          <p className="flex items-center gap-2 text-body font-semibold text-slate-800 dark:text-slate-100">
            <DollarSign size={18} aria-hidden="true" className="text-brand-600" />
            {displayName}
          </p>
          <p className="mt-0.5 text-chip text-slate-500 dark:text-slate-400">{email}</p>
          <p className="mt-2 text-field text-slate-600 dark:text-slate-300">
            {record && cls
              ? t('gest:admin.remuneration.summaryLine', {
                  classLabel: cls.label,
                  echelon: record.offGrid
                    ? t('gest:management.class.offGridShort')
                    : String(record.currentEchelon),
                  annual:
                    hub?.timeline?.currentAnnual != null
                      ? money(hub.timeline.currentAnnual)
                      : '—',
                })
              : t('gest:admin.remuneration.notConfigured')}
          </p>
          {record?.offGrid && record.offGridReasonPrivate ? (
            <p className="mt-1 text-chip text-amber-700 dark:text-amber-300">
              {t('gest:admin.remuneration.offGridPrivateHint')}
            </p>
          ) : null}
        </div>
        <Link
          to={profileHref}
          className={`${GS_COMPONENTS.btnSecondary} inline-flex items-center gap-1.5`}
        >
          <Eye size={16} aria-hidden="true" />
          {t('gest:admin.remuneration.viewProfile')}
        </Link>
      </div>

      {/* key=email sur le parent remonte ClassAssignForm (P-ADM-16 hydrate frais). */}
      <div className="flex flex-col gap-4">
        <ClassGrid
          email={email}
          record={record}
          classes={comp.classes}
          onError={onError}
          action={
            <SalaryHistoryDialog
              email={email}
              entries={comp.salaryHistory}
              currentYear={year}
              onError={onError}
            />
          }
        />
        {d.can.setHourlyRate ? (
          <RealRateCard
            email={email}
            rateHistory={comp.rateHistory}
            offGrid={record?.offGrid ?? false}
            onError={onError}
          />
        ) : null}
        <RatePremiumCard
          email={email}
          premiums={premiums}
          baseWeeklyHours={baseWeekly}
          overrides={overrides}
          year={year}
          onError={onError}
        />
        <HoursCard
          email={email}
          comp={comp}
          currentYear={year}
          specialRate={specialRate}
          onError={onError}
        />
        <BenefitsCard email={email} comp={comp} currentYear={year} onError={onError} />
        <BonusCard
          email={email}
          bonusList={comp.bonusList}
          bonusBase={bonusBase}
          currentYear={year}
          onError={onError}
        />
      </div>
    </div>
  );
}
